import { computed, ref } from 'vue';
import { Assistent } from '@keyvue/one-api-client';
import axios from 'axios';

const assistent = ref<Assistent>();

export default function useConfiguration() {
    const isLoaded = computed(() => !!assistent.value?.id);
    const isPreview = ref<boolean>(document.location.search.includes('preview'));
    const withoutBranding = computed(() => assistent.value?.features.withoutBranding ?? false);

    function updateStyling() {
        const widget = assistent.value?.widget;
        if (!widget) {
            return;
        }

        let root = document.documentElement;
        const shadowRoot = document.getElementById('embed-assistent')?.shadowRoot;

        if (shadowRoot) {
            const newRoot = shadowRoot.querySelector('div');
            if (newRoot) {
                root = newRoot;
            }
        }

        root.style.setProperty('--color-widget', widget.color);
        root.style.setProperty('--color-balloon', widget.balloonColor);
    }

    function updateAssistent(value: Assistent) {
        assistent.value = value;
        updateStyling();
    }

    async function loadFromConfigurationId(configurationId: string) {
        const assistentData = JSON.parse(document.getElementById(configurationId)?.innerHTML ?? '{}');
        const parsed = Assistent.fromObject(assistentData);

        if (!parsed.active && !isPreview.value) {
            return;
        }

        updateAssistent(parsed);
    }

    async function loadFromApi(assistentId: string) {
        const baseUrl = `${import.meta.env.VITE_API_HOST}${import.meta.env.VITE_API_BASEPATH}`;
        const response = await axios.get(`${baseUrl}/ai/assistent/assistents/${assistentId}`);
        const parsed = Assistent.fromObject(response.data);

        if (!parsed.active && !isPreview.value) {
            return;
        }

        updateAssistent(parsed);
    }

    // debug
    async function load() {
        await loadFromConfigurationId('embed-assistent-configuration');

        if (!assistent.value?.id) {
            const id = new URLSearchParams(document.location.search).get('assistent');
            if (id) {
                await loadFromApi(id);
            }
        }

        if (!assistent.value?.id) {
            throw new Error('Unable to load assistent');
        }
    }

    return {
        load,
        updateAssistent,
        assistent,
        isLoaded,
        isPreview,
        withoutBranding,
    };
}
